import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

/**
 * SpecForma is in beta — pricing is not public yet, so every plan routes to
 * "Contact us" rather than showing a number. Tier → module model: "Specs" =
 * forma + spec manager; "Projects" = all modules.
 */
const CONTACT = 'mailto:info@specforma.app?subject=SpecForma%20pricing';

const TIERS = [
  {
    name: 'Specs',
    blurb: 'Master + project specifications with the Forma formatting engine.',
    featured: false,
    features: [
      'Master specifications (core + variants)',
      'Project specs & compile',
      'Forma formatting engine (Word export)',
      'Draft → review → issue workflow',
      'Roles & permissions',
    ],
  },
  {
    name: 'Projects',
    blurb: 'Everything in Specs, plus project management built for A/E delivery.',
    featured: true,
    features: [
      'Everything in Specs',
      'Project & phase management',
      'Hour forecasting & tracking',
      'Team & organization management',
      'Reporting & dashboards',
      'AI-assisted authoring',
    ],
  },
  {
    name: 'Enterprise',
    blurb: 'For larger firms with SSO, advanced security, and onboarding needs.',
    featured: false,
    features: [
      'Everything in Projects',
      'SSO / SAML (on request)',
      'Advanced security & SLAs',
      'Guided onboarding & migration',
      'Priority support',
    ],
  },
];

export function PricingPage() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight text-text">Pricing</h1>
        <p className="mt-4 text-lg text-muted">
          SpecForma is in beta, so pricing isn’t public yet. Tell us about your firm and we’ll put
          together the right plan — every plan includes the Forma formatting engine.
        </p>
      </div>

      <div className="mt-16 grid gap-6 lg:grid-cols-3">
        {TIERS.map((t) => (
          <div
            key={t.name}
            className={cn(
              'flex flex-col rounded-2xl border bg-surface p-8',
              t.featured ? 'border-primary shadow-lg' : 'border-border'
            )}
          >
            {t.featured ? (
              <span className="mb-4 inline-flex w-fit rounded-full bg-primary px-3 py-1 text-xs font-semibold text-inverse">
                Most popular
              </span>
            ) : null}
            <h2 className="text-xl font-semibold text-text">{t.name}</h2>
            <p className="mt-2 text-md text-muted">{t.blurb}</p>
            <div className="mt-6">
              <span className="text-2xl font-bold tracking-tight text-text">
                Contact for pricing
              </span>
            </div>
            <Button
              asChild
              variant={t.featured ? 'default' : 'outline'}
              size="lg"
              className="mt-6 w-full"
            >
              <a href={CONTACT}>Contact us</a>
            </Button>
            <ul className="mt-8 flex flex-col gap-3">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-md text-text">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-12 text-center text-md text-muted">
        Not sure which plan fits?{' '}
        <a
          href={CONTACT}
          className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Talk to us
        </a>{' '}
        and we’ll help you choose.
      </p>
    </div>
  );
}
